import React from 'react';
import styled from '@emotion/styled';
import { Box, Flex, Text, Button, Palette } from '@updater/ui-uds';
import {
  X,
  Plus,
  CheckCircle,
  DotsThreeCircle,
  Circle,
} from '@phosphor-icons/react';
import { InitiatePayload } from 'flows/core/types/cards';
import { isTaskCompletedOrValidating } from 'flows/core/utils/card-makers/utils';
import { ItemStatuses } from 'constants/statuses';
import { ChatColors } from '../../../configs/chat-colors';

type TasksSidebarProps = {
  isOpen: boolean;
  onClose: () => void;
  onNewConversation?: () => void;
  onSelectTask?: (taskName: string) => void;
  priorityCards: InitiatePayload[];
  completedCards: InitiatePayload[];
  currentMoveDate: string | null | undefined;
};

const Backdrop = styled(Box)<{ isVisible: boolean; shouldFadeOut: boolean }>`
  position: absolute;
  inset: 0;
  background: ${ChatColors.panel.BACKDROP_BG};
  z-index: 998; /* Very high z-index to ensure it's above everything else */
  opacity: ${({ isVisible, shouldFadeOut }) => {
    if (!isVisible && !shouldFadeOut) return 0; // Hidden when completely closed
    if (shouldFadeOut) return 0; // Start fade out
    return 1; // Stay visible during slide-out until fade starts
  }};
  pointer-events: ${({ isVisible, shouldFadeOut }) =>
    isVisible || shouldFadeOut ? 'auto' : 'none'};
  /* Conditional transition: no fade-in (instant), delayed fade-out after panel slides out */
  transition: none;
`;

const Panel = styled(Box)<{ isVisible: boolean }>`
  position: fixed !important;
  top: 0;
  right: 0;
  height: 100vh !important;
  width: min(92vw, 380px);

  /* Frame 14592 */

  box-sizing: border-box;

  /* Auto layout */
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;

  position: absolute;
  width: 344px;
  height: 956px;
  right: 0px;
  top: 0px;

  background-image: radial-gradient(
    100% 100% at 3.63% 100%,
    ${ChatColors.panel.GRADIENT_START} 0%,
    ${ChatColors.panel.GRADIENT_MID} 50%,
    ${ChatColors.panel.GRADIENT_END} 100%
  );

  color: ${ChatColors.panel.STATUS_WHITE};
  box-shadow: -8px 0 24px ${ChatColors.panel.SHADOW};
  z-index: 999; /* Highest z-index to ensure it's the topmost layer */
  display: flex;
  text-align: left;
  flex-direction: column;
  transform: ${({ isVisible }) =>
    isVisible ? 'translateX(0)' : 'translateX(100%)'};
  transition: transform 0.3s ease-in-out;
  /* Ensure it doesn't affect main content layout */
  pointer-events: ${({ isVisible }) => (isVisible ? 'auto' : 'none')};
  will-change: transform; /* Optimize for animation performance */
`;

const Header = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1rem 0.5rem;
  width: 100%;
`;

const Content = styled(Box)`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0.75rem 0 1rem 0;
  width: 100%;
`;

const TaskItem = styled(Flex)`
  align-items: start;
  gap: 0.5rem;
  padding: 1rem;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;
  &:hover {
    background-color: ${ChatColors.panel.TASKITEM_HOVER_BG};
  }
  &:focus-visible {
    outline: 2px solid ${ChatColors.panel.OUTLINE_COLOR};
    outline-offset: 2px;
  }
`;

const Footer = styled(Box)`
  width: 100%;
  padding: 0.75rem 1rem calc(env(safe-area-inset-bottom, 0px) + 0.5rem) 1rem;
  border-top: 1px solid ${ChatColors.panel.BORDER_COLOR};
  display: flex;
  justify-content: center;
`;

// Match ChatInterface header action button look
const OutlineButton = styled(Button)`
  align-items: start;
  border: none;
  color: ${ChatColors.panel.STATUS_WHITE};
  background-color: transparent;
  padding: 0.375rem 0.375rem;
  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
  &:hover {
    background-color: transparent;
    color: ${ChatColors.panel.PRIMARY_BLUE};
  }
`;

// Ensure icon vertically centers relative to text
const IconCell = styled(Box)`
  width: 20px;
  height: 24px;
`;

export const TasksSidebar: React.FC<TasksSidebarProps> = ({
  isOpen,
  onClose,
  onNewConversation,
  onSelectTask,
  priorityCards,
  completedCards,
}: TasksSidebarProps) => {
  const [isVisible, setIsVisible] = React.useState(false);
  const [animationState, setAnimationState] = React.useState<
    'closed' | 'opening' | 'open' | 'closing'
  >('closed');
  const [backdropShouldFade, setBackdropShouldFade] = React.useState(false);

  // Debounce rapid state changes
  const [debouncedIsOpen, setDebouncedIsOpen] = React.useState(isOpen);
  const debounceTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  React.useEffect(() => {
    // Clear any existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Set new timeout to debounce the isOpen prop
    debounceTimeoutRef.current = setTimeout(() => {
      setDebouncedIsOpen(isOpen);
    }, 10); // Small debounce delay

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [isOpen]);

  React.useEffect(() => {
    console.log('🎭 Animation effect triggered:', {
      debouncedIsOpen,
      animationState,
      isOpen,
    });

    if (debouncedIsOpen && animationState === 'closed') {
      // Opening: start animation immediately
      setIsVisible(true);
      setAnimationState('opening');
      setTimeout(() => {
        setAnimationState('open');
      }, 10);
    } else if (!debouncedIsOpen) {
      // As soon as sidebar is triggered to close, hide overlay instantly
      setIsVisible(false);
      if (animationState === 'open' || animationState === 'closing') {
        setAnimationState('closing');
        setTimeout(() => {
          setAnimationState('closed');
        }, 300);
      }
    }
  }, [debouncedIsOpen, animationState, isOpen]);

  // Custom close handler to ensure proper animation sequence
  const handleClose = React.useCallback(() => {
    // Call onClose immediately so sidebar closes right away
    onClose();
    // Optionally, still trigger animation for visual effect
    if (animationState === 'open') {
      setAnimationState('closing');
      setIsVisible(false);
      setTimeout(() => {
        setAnimationState('closed');
      }, 300);
    }
  }, [animationState, onClose]);

  // Manage backdrop fade timing - start fade when panel slide-out completes
  React.useEffect(() => {
    if (animationState === 'closed' && !debouncedIsOpen) {
      setBackdropShouldFade(true);

      setTimeout(() => {
        setBackdropShouldFade(false);
      }, 200);
    } else if (debouncedIsOpen) {
      // Opening or staying open - reset backdrop fade state
      setBackdropShouldFade(false);
    }
  }, [animationState, debouncedIsOpen]);

  // Move 'In review' cards to completedCards
  const isInReview = (card: InitiatePayload) => {
    const statusRaw = card?.data?.status as string | undefined;
    return statusRaw === ItemStatuses.VALIDATING;
  };

  // Sort so STARTED/PENDING are last
  const filteredPriorityCards = priorityCards
    .filter((card) => !isInReview(card))
    .sort((a, b) => {
      const aStatus = a?.data?.status;
      const bStatus = b?.data?.status;
      const isAStartedOrPending =
        aStatus === ItemStatuses.STARTED || aStatus === ItemStatuses.PENDING;
      const isBStartedOrPending =
        bStatus === ItemStatuses.STARTED || bStatus === ItemStatuses.PENDING;
      if (isAStartedOrPending && !isBStartedOrPending) return 1;
      if (!isAStartedOrPending && isBStartedOrPending) return -1;
      return 0;
    });
  // Sort completed so that 'Complete' status is last
  const completedWithReview = [
    ...completedCards,
    ...priorityCards.filter(isInReview),
  ].sort((a, b) => {
    const aStatus = a?.data?.status;
    const bStatus = b?.data?.status;
    // Put 'Complete' last
    if (
      aStatus === ItemStatuses.COMPLETED &&
      bStatus !== ItemStatuses.COMPLETED
    )
      return 1;
    if (
      aStatus !== ItemStatuses.COMPLETED &&
      bStatus === ItemStatuses.COMPLETED
    )
      return -1;
    return 0;
  });

  const renderTaskRow = (card: InitiatePayload) => {
    const title = card?.data?.title ?? 'Task';
    const statusDone = isTaskCompletedOrValidating(card?.data);
    const statusRaw = card?.data?.status as string | undefined;
    const { statusText, statusColor, iconColor } = (() => {
      if (statusDone) {
        if (statusRaw === ItemStatuses.VALIDATING) {
          return {
            statusText: 'Complete - in review',
            statusColor: ChatColors.panel.TASK_COMPLETE_GREEN,
            iconColor: ChatColors.panel.TASK_COMPLETE_GREEN,
          } as const;
        }
        return {
          statusText: 'Complete',
          statusColor: ChatColors.panel.TASK_COMPLETE_GREEN,
          iconColor: ChatColors.panel.TASK_COMPLETE_GREEN,
        } as const;
      }
      if (
        statusRaw === ItemStatuses.STARTED ||
        statusRaw === ItemStatuses.PENDING
      )
        return {
          statusText: 'In progress',
          statusColor: ChatColors.panel.TASK_IN_PROGRESS_BLUE,
          iconColor: ChatColors.panel.TASK_IN_PROGRESS_BLUE,
        } as const;
      if (statusRaw === 'In review')
        return {
          statusText: 'In review',
          statusColor: ChatColors.panel.TASK_IN_PROGRESS_BLUE,
          iconColor: ChatColors.panel.TASK_IN_PROGRESS_BLUE,
        } as const;
      return {
        statusText: 'Not started',
        statusColor: ChatColors.panel.TASK_NOT_STARTED_GREY,
        iconColor: ChatColors.panel.TASK_NOT_STARTED_GREY,
      } as const;
    })();
    return (
      <TaskItem
        key={card.identifier}
        role="button"
        tabIndex={0}
        aria-label={`Open task ${title}`}
        onClick={() => {
          onSelectTask?.(title);
          onClose();
        }}
        onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelectTask?.(title);
            onClose();
          }
        }}
      >
        <IconCell aria-hidden>
          {statusDone || statusRaw === 'In review' ? (
            <CheckCircle color={iconColor} size={18} weight="duotone" />
          ) : statusRaw === ItemStatuses.STARTED ||
            statusRaw === ItemStatuses.PENDING ? (
            <DotsThreeCircle color={iconColor} size={18} weight="duotone" />
          ) : (
            <Circle color={iconColor} opacity={0.64} size={18} />
          )}
        </IconCell>
        <Box style={{ flex: 1 }}>
          <Text
            as="span"
            variant="sBold"
            color={Palette.white}
            style={{ display: 'block', lineHeight: 1.25 }}
          >
            {title}
          </Text>
          <Text
            as="span"
            variant="xs"
            color={statusColor}
            style={{ display: 'block', marginTop: '2px' }}
          >
            {statusText}
          </Text>
        </Box>

        {/* remove for now
         {card?.data?.badge ? (
          <Badge
            variant={card.data.badge.variant}
            text={card.data.badge.text}
          />
        ) : null} */}
      </TaskItem>
    );
  };

  // Always render both backdrop and panel, control visibility with CSS
  return (
    <>
      <Backdrop
        isVisible={animationState !== 'closed'}
        shouldFadeOut={backdropShouldFade}
        onClick={handleClose}
        data-testid="tasks-sidebar-backdrop"
      />
      <Panel isVisible={isVisible} role="dialog" aria-label="Tasks sidebar">
        <Header>
          <Box>
            <Text variant="lBold" color={Palette.white} as="h2">
              Your tasks
            </Text>
            <Text variant="s" color={Palette.white} as="h3" opacity=".88">
              Jump to any task
            </Text>
          </Box>
          <OutlineButton
            aria-label="Close sidebar"
            type="button"
            onClick={handleClose}
          >
            <X size={24} />
          </OutlineButton>
        </Header>
        <Content>
          <Box style={{ marginBottom: '0.5rem' }}></Box>

          <Box as="div" role="list" aria-label="Priority tasks">
            {filteredPriorityCards?.map(renderTaskRow)}
          </Box>

          {completedWithReview?.length ? (
            <Box>
              <Box as="div" role="list" aria-label="Completed tasks">
                {completedWithReview.map(renderTaskRow)}
              </Box>
            </Box>
          ) : null}
        </Content>
        <Footer>
          <OutlineButton
            isFluidWidth
            onClick={async () => {
              try {
                await onNewConversation?.();
              } finally {
                onClose();
              }
            }}
            size="m"
            data-testid="sidebar-new-conversation"
            type="button"
          >
            <Flex justifyContent="flex-start" gap="xs" width="100%">
              <Plus size={18} />
              <span>New conversation</span>
            </Flex>
          </OutlineButton>
        </Footer>
      </Panel>
    </>
  );
};

export default TasksSidebar;
